/**
 * =============================================================================
 *  LAM MUOT NET VE TAY
 * =============================================================================
 *
 *  Su kien con tro ve toi o tan so man hinh, nen mot net ve ba giay cho ra vai
 *  tram diem, phan lon cach nhau duoi mot diem PDF. Ghi thang chung vao
 *  /InkList cho ra hai van de:
 *
 *    - net rung (moi rung tay nho deu thanh mot goc gay trong duong ve),
 *    - /InkList phinh to vo ich; mot trang vai chuc net thanh vai tram kilobyte.
 *
 *  Ba buoc, theo dung thu tu:
 *      1. LOC THUA   - bo diem cach diem truoc duoi nguong.
 *      2. CHAIKIN    - cat goc hai lan; moi lan bien mot goc thanh hai diem o
 *                      1/4 va 3/4 canh. Duong ket qua hoi tu ve duong B-spline
 *                      bac hai va KHONG bao gio vuot ra ngoai bao loi cua cac
 *                      diem goc - nen no khong the "phinh" ra ngoai vung nguoi
 *                      dung da ve.
 *      3. GIU HAI DAU - Chaikin cat ca hai dau mut; ta dat lai diem dau va diem
 *                      cuoi that, de net bat dau va ket thuc dung cho nguoi
 *                      dung nhac but len.
 *
 * =============================================================================
 *  AP LUC BUT BI BO QUA - CO Y, KHONG PHAI THIEU SOT
 * =============================================================================
 *  PointerEvent co `pressure`, va no rat cam do. Nhung /Ink cua PDF khong co
 *  cho nao mang be day theo tung diem: ca net dung DUNG MOT gia tri /BS /W.
 *  Cach duy nhat de dien ta ap luc la bo /Ink va ve mot vung to (/Path hoac
 *  mot day hinh) - luc do no khong con la chu thich sua duoc trong Acrobat nua,
 *  tuc la mat dung cai ly do ta chon /Ink that thay vi ve de len.
 *
 *  Nen: ap luc bi bo, va giao dien khong duoc hua hen no.
 */
import type { PdfPoint } from '@pdfeditor/types';

/** Diem cach nhau duoi nguong nay (don vi diem PDF) bi coi la trung. */
export const MIN_POINT_GAP_PT = 0.75;

/** So lan cat goc Chaikin. Hai la du muot; ba thi so diem tang gap tam. */
export const SMOOTH_PASSES = 2;

/** Tran so diem cua MOT net sau khi lam muot. Chan net dai bat thuong. */
export const MAX_POINTS_PER_STROKE = 2000;

function distance(a: PdfPoint, b: PdfPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Buoc 1: bo cac diem qua sat nhau. Luon giu diem dau va diem cuoi. */
export function decimate(points: readonly PdfPoint[], minGapPt = MIN_POINT_GAP_PT): PdfPoint[] {
  if (points.length <= 2) return points.map((p) => ({ ...p }));
  const out: PdfPoint[] = [{ ...(points[0] as PdfPoint) }];
  for (let i = 1; i < points.length - 1; i++) {
    const point = points[i] as PdfPoint;
    if (distance(point, out[out.length - 1] as PdfPoint) >= minGapPt) {
      out.push({ ...point });
    }
  }
  out.push({ ...(points[points.length - 1] as PdfPoint) });
  return out;
}

/** Mot lan cat goc Chaikin. */
function chaikinPass(points: readonly PdfPoint[]): PdfPoint[] {
  if (points.length < 3) return points.map((p) => ({ ...p }));
  const out: PdfPoint[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i] as PdfPoint;
    const b = points[i + 1] as PdfPoint;
    out.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
    out.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
  }
  return out;
}

/**
 * Lam muot MOT net. Ham thuan tuy; mang dau vao khong bi sua.
 *
 * Net hai diem duoc tra ve nguyen ven: mot doan thang da la duong muot nhat co
 * the, va cat goc no chi lam no ngan lai.
 */
export function smoothStroke(points: readonly PdfPoint[], passes = SMOOTH_PASSES): PdfPoint[] {
  const thinned = decimate(points);
  if (thinned.length <= 2) return thinned;

  const first = thinned[0] as PdfPoint;
  const last = thinned[thinned.length - 1] as PdfPoint;

  let current: PdfPoint[] = thinned;
  for (let i = 0; i < passes; i++) {
    if (current.length >= MAX_POINTS_PER_STROKE) break;
    current = chaikinPass(current);
  }

  // Buoc 3: dat lai hai dau mut that.
  current[0] = { ...first };
  current[current.length - 1] = { ...last };

  if (current.length > MAX_POINTS_PER_STROKE) {
    const step = Math.ceil(current.length / MAX_POINTS_PER_STROKE);
    const trimmed = current.filter((_, i) => i % step === 0);
    trimmed[0] = { ...first };
    trimmed[trimmed.length - 1] = { ...last };
    return trimmed;
  }
  return current;
}

/** Lam muot ca mot chum net. Net duoi hai diem bi loai - xem support.ts. */
export function smoothInkPaths(paths: readonly (readonly PdfPoint[])[]): PdfPoint[][] {
  return paths.filter((p) => p.length >= 2).map((p) => smoothStroke(p));
}
